import React from 'react'
import {useState, useEffect, useContext} from 'react'
import {usePrevious} from 'react-use';
import CloseIcon from '@mui/icons-material/Close';
import Intro from './Intro'
import Devices from './Devices'
import Call from './Call'
import CallEnd from './CallEnd'
import PreCallForm from './PreCallForm';
import FeedbackForm from './FeedbackForm';
import ConfigContext from '../contexts/configContext'
import PreConference from './PreConference';
import LWCLogo from '../images/lwc.png'

export default function CallSetup({meetingId, pin, onClose, onMeetingIdChange, onPINChange}) {
  const config = useContext(ConfigContext)
  const [cameraAllowed, setCameraAllowed] = useState(true)
  const [hasDummyVideoTrack, setHasDummyVideoTrack] = useState(false)
  const [currentPage, setCurrentPage] = useState(null)
  const [sipHeaders, setSipHeaders] = useState([])
  const [extension, setExtension] = useState("9000")
  const [callerName, setCallerName] = useState('Live Call User')
  const [localStream, setLocalStream] = useState(null)
  const [localAudioInputId, setLocalAudioInputId] = useState(null)
  const [localVideoInputId, setLocalVideoInputId] = useState(null)
  const [audioDevices, setAudioDevices] = useState([])
  const [videoDevices, setVideoDevices] = useState([])
  const prevLocalStream = usePrevious(localStream);
  const [hasIntro, setHasIntro] = useState(false)
  const [hasPreCallForm, setHasPreCallForm] = useState(false)
  const [conferenceAPISupport, setConferenceAPISupport] = useState(false)
  const [conferenceAPIDomain, setConferenceAPIDomain] = useState(null)
  const [conferenceSettings, setConferenceSettings] = useState(null)
  const [displayDMCTag, setDisplayDMCTag] = useState(true)

  useEffect(() => {
    if (!config) return
    if (config.intro?.hasOwnProperty('enabled')) {
      setHasIntro(config.intro.enabled)
    }
    if (config.hasOwnProperty('precallform')) {
      if (config.precallform.hasOwnProperty('enabled')) {
        setHasPreCallForm(config.precallform.enabled)
      } else {
        setHasPreCallForm(true)
      }
    }
    if (config.devices?.hasOwnProperty('videoEnabled')) setCameraAllowed(config.devices.videoEnabled)
    if (config.api?.enabled && config.api?.domain) {
      setConferenceAPISupport(true)
      setConferenceAPIDomain(config.api.domain)
    }
    if (config.hasOwnProperty('displayDMCTag')) setDisplayDMCTag(config.displayDMCTag)
  }, [config])

  useEffect(() => {
    if (!conferenceSettings) return
    console.log(conferenceSettings)
    setCameraAllowed(conferenceSettings.enable_video)
  }, [conferenceSettings])

  useEffect(() => {
    if (meetingId) {
      if (hasPreCallForm) {
        setCurrentPage('precallform')
      } else {
        setCurrentPage('devices')
      }
    } else if (hasIntro) {
      setCurrentPage('intro')
    } else if (hasPreCallForm) {
      setCurrentPage('precallform')
    } else {
      setCurrentPage('devices')
    }
  }, [meetingId, hasIntro, hasPreCallForm])

  useEffect(() => {
    if (prevLocalStream) {
      stopAllTracks(prevLocalStream)
    }
  }, [localStream, prevLocalStream])

  const stopAllTracks = (stream) => {
    stream.getTracks().forEach(track => track.stop())
  }

  const renderPage = (page) => {
    let component = null
    if (page === "intro") {
       component = <Intro 
                        onStart={() => {
                          if (hasPreCallForm) {
                            setCurrentPage('precallform')
                          } else {
                            setCurrentPage('devices')
                          }
                        }}
                        onLearnMore={() => setCurrentPage('learnmore')}
                   />
    } else if (page === 'precallform') {
       component = <PreCallForm 
                        meetingId={meetingId}
                        pin={pin}
                        onSubmit={() => {
                          //setCurrentPage('devices')
                          conferenceAPISupport ? setCurrentPage('preconference') : setCurrentPage('devices')
                        }}
                        onSipHeadersChange={(headers) => {
                          let headerArray = []
                          Object.keys(headers).forEach(key => {
                            headerArray.push(`${key}: ${headers[key]}`)
                          });
                          setSipHeaders(headerArray)
                        }}
                        onExtensionChange={(ext) => {
                          setExtension(ext)
                        }}
                        onCallerNameChange={(name) => {
                          setCallerName(name)
                        }}
                        onMeetingIdChange={onMeetingIdChange}
                        onPINChange={onPINChange}
                   />
    } else if (page === 'preconference') {
       component = <PreConference
                        meetingId={meetingId}
                        conferenceAPIDomain={conferenceAPIDomain}
                        onReady={(settings) => {
                          setConferenceSettings(settings)
                          setCurrentPage('devices')
                        }}
                        onBack={() => setCurrentPage('precallform')}
                        onClose={() => onClose()}
                   />
    } else if (page === 'devices') {
       component = <Devices 
                        cameraAllowed={cameraAllowed}
                        onBegin={() => setCurrentPage('call')}
                        onStream={(stream, audioInput, videoInput, dummyVideoTrack) => {
                          setLocalStream(stream)
                          setLocalAudioInputId(audioInput)
                          setLocalVideoInputId(videoInput)
                          setHasDummyVideoTrack(dummyVideoTrack)
                        }}
                        onAudioDevices={setAudioDevices}
                        onVideoDevices={setVideoDevices}
                   />
    } else if (page === 'call') {
       component = <Call 
                        cameraAllowed={cameraAllowed}
                        hasDummyVideoTrack={hasDummyVideoTrack}
                        meetingId={meetingId}
                        pin={pin}
                        extension={extension}
                        callerName={callerName}
                        extraHeaders={sipHeaders}
                        localStream={localStream}
                        localAudioInputId={localAudioInputId}
                        localVideoInputId={localVideoInputId}
                        audioDevices={audioDevices}
                        videoDevices={videoDevices}
                        conferenceSettings={conferenceSettings}
                        onCallEnd={() => {
                          if (!conferenceSettings || conferenceSettings.enable_feedback) {
                            setCurrentPage('feedbackform')
                          } else {
                            setCurrentPage('callend')
                          }
                          if (localStream) stopAllTracks(localStream)
                        }}
                        onAudioInputChange={(deviceId) => setLocalAudioInputId(deviceId)}
                        onVideoInputChange={(deviceId) => setLocalVideoInputId(deviceId)}
                        setHasDummyVideoTrack={setHasDummyVideoTrack}
                   />
    } else if (page === 'feedbackform') {
       component = <FeedbackForm
                        onSubmit={() => setCurrentPage('callend')}
                        onSkip={() => setCurrentPage('callend')}
                   />
    } else if (page === 'callend') {
       component = <CallEnd 
                        onFinish={() => onClose()}
                        onRejoin={() => setCurrentPage('devices')}
                   />
    }

    return component
  }

  return (
    <div className='live-call-call-setup-outer'>
        <div className='live-call-call-setup-window'>
                {!meetingId && currentPage !== "call" ?
                  <div className='live-call-call-setup-close'>
                      <CloseIcon
                          color='primary'
                          onClick={() => {
                            if (localStream) {
                              stopAllTracks(localStream)
                            }
                            onClose()
                          }}
                      />
                  </div>
                :null}
            <div className='live-call-call-setup-content-container'>
                {renderPage(currentPage)}
            </div>
            {displayDMCTag ? 
              <div className='live-call-call-setup-dmc-tag'>
                <img src={LWCLogo} style={{}}/>
              </div>
            : null
            }
        </div>
    </div>
  )
}
